import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import Cookies from "js-cookie";
import { Star, GraduationCap, Building2 } from "lucide-react";
import apiUrl from "../utility/apiUrl";
import LoginPopup from "./LoginPopup";
import { RevealGroup, revealItem } from "./ui/Reveal";
import Button from "./ui/Button";

const StarRow = ({ value, size = 16, className = "" }) => (
  <span className={`inline-flex items-center gap-0.5 ${className}`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        width={size}
        height={size}
        className={i <= Math.round(value || 0) ? "text-amber-400" : "text-gray-300"}
        fill={i <= Math.round(value || 0) ? "currentColor" : "none"}
      />
    ))}
  </span>
);

const StarPicker = ({ value, onChange }) => (
  <span className="inline-flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type="button"
        onClick={() => onChange(i)}
        aria-label={`Rate ${i} out of 5`}
        className="p-0.5"
      >
        <Star
          width={24}
          height={24}
          className={i <= value ? "text-amber-400" : "text-gray-300"}
          fill={i <= value ? "currentColor" : "none"}
        />
      </button>
    ))}
  </span>
);

const TESTIMONIAL_CLAMP_LENGTH = 180;

const TestimonialCard = ({ t }) => {
  const [expanded, setExpanded] = useState(false);
  const isCollege = t.reviewerType === "COLLEGE";
  const reviewText = t.reviewText || "";
  const isLong = reviewText.length > TESTIMONIAL_CLAMP_LENGTH;

  return (
    <motion.div
      variants={revealItem}
      className="rounded-2xl border border-gray-200 bg-white p-4 flex flex-col"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`inline-flex h-8 w-8 flex-none items-center justify-center rounded-full ${
              isCollege ? "bg-purple-50 text-purple-600" : "bg-brand-50 text-brand-600"
            }`}
          >
            {isCollege ? <Building2 className="h-4 w-4" /> : <GraduationCap className="h-4 w-4" />}
          </span>
          <div className="min-w-0">
            <div className="font-medium text-gray-900 truncate">{t.reviewerName}</div>
            <div className="text-xs text-gray-500">{isCollege ? "College" : "Student"}</div>
          </div>
        </div>
        <StarRow value={t.rating} />
      </div>
      <p
        className={`mt-3 text-sm text-gray-700 whitespace-pre-line flex-1 ${
          isLong && !expanded ? "line-clamp-4" : ""
        }`}
      >
        {reviewText}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-2 self-start text-xs font-semibold text-brand-600 hover:text-brand-700"
        >
          {expanded ? "View less" : "View more"}
        </button>
      )}
    </motion.div>
  );
};

const statusBanner = (mine) => {
  if (!mine) return null;
  if (mine.status === "PENDING_REVIEW") {
    return {
      tone: "bg-amber-50 border-amber-200 text-amber-800",
      text: "Your testimonial is awaiting admin approval.",
    };
  }
  if (mine.status === "REJECTED") {
    return {
      tone: "bg-red-50 border-red-200 text-red-800",
      text: mine.rejectionReason
        ? `Your testimonial wasn't approved: ${mine.rejectionReason}`
        : "Your testimonial wasn't approved. You can edit and resubmit it below.",
    };
  }
  return {
    tone: "bg-green-50 border-green-200 text-green-800",
    text: "Your testimonial is live on this page.",
  };
};

// Platform testimonials — Student/College feedback about using BharatVidyarthi
// itself (not a review of a specific college — that feature was removed).
const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [averageRating, setAverageRating] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [mine, setMine] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formRating, setFormRating] = useState(5);
  const [formText, setFormText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showLogin, setShowLogin] = useState(false);

  const token = Cookies.get("token");
  const accountType = Cookies.get("accountType");
  const canSubmit = Boolean(token) && (accountType === "student" || accountType === "college");

  const loadTestimonials = useCallback(() => {
    fetch(`${apiUrl}/api/v1/testimonial/list?page=1&size=12`, {
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => res.json())
      .then((data) => {
        setTestimonials(Array.isArray(data?.testimonials) ? data.testimonials : []);
        setAverageRating(data?.averageRating ?? null);
        setTotalCount(data?.totalCount ?? 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadMine = useCallback(() => {
    if (!canSubmit) return;
    fetch(`${apiUrl}/api/v1/testimonial/mine`, {
      headers: { Authorization: token },
    })
      .then((res) => (res.status === 204 ? null : res.json()))
      .then((data) => {
        if (data) {
          setMine(data);
          setFormRating(data.rating || 5);
          setFormText(data.reviewText || "");
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSubmit]);

  useEffect(() => {
    loadTestimonials();
  }, [loadTestimonials]);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  const submitTestimonial = async (e) => {
    e.preventDefault();
    if (!formText.trim()) {
      setSubmitError("Please write a few words about your experience.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch(`${apiUrl}/api/v1/testimonial`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ rating: formRating, reviewText: formText.trim() }),
      });
      if (!res.ok) throw new Error("Submit failed");
      const data = await res.json();
      setMine(data);
      setShowForm(false);
    } catch {
      setSubmitError("Couldn't submit your testimonial. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const banner = statusBanner(mine);

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">
            What Our Students &amp; Colleges Say
          </h2>
          <p className="text-sm text-gray-600">Real experiences from people using BharatVidyarthi</p>
        </div>
        {totalCount > 0 ? (
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <StarRow value={averageRating} />
            <span className="font-semibold text-gray-900">{averageRating}</span>
            <span className="text-gray-500">
              ({totalCount} review{totalCount === 1 ? "" : "s"})
            </span>
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
          <div className="h-32 rounded-2xl bg-gray-100" />
          <div className="h-32 rounded-2xl bg-gray-100" />
          <div className="h-32 rounded-2xl bg-gray-100" />
        </div>
      ) : testimonials.length > 0 ? (
        <RevealGroup className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {testimonials.map((t) => (
            <TestimonialCard key={t.id} t={t} />
          ))}
        </RevealGroup>
      ) : (
        <p className="mt-4 text-sm text-gray-700">
          No testimonials yet — be the first to share your experience.
        </p>
      )}

      <div className="mt-6 border-t border-gray-200 pt-4">
        {!token ? (
          <>
            <p className="text-sm text-gray-600 mb-2">
              Are you a student or a college partner? Share your experience with BharatVidyarthi.
            </p>
            <Button type="button" onClick={() => setShowLogin(true)} variant="primary">
              Log in to write a testimonial
            </Button>
            {showLogin && (
              <LoginPopup
                onClose={() => setShowLogin(false)}
                onDefaultLogin={() => setShowLogin(false)}
                defaultAccountType="student"
              />
            )}
          </>
        ) : !canSubmit ? (
          <p className="text-sm text-gray-500">Only student or college accounts can submit a testimonial.</p>
        ) : (
          <>
            {banner && (
              <div className={`mb-3 rounded-xl border px-4 py-3 text-sm ${banner.tone}`}>{banner.text}</div>
            )}

            {!showForm ? (
              <Button type="button" onClick={() => setShowForm(true)} variant="primary">
                {mine ? "Edit your testimonial" : "Write a testimonial"}
              </Button>
            ) : (
              <form onSubmit={submitTestimonial} className="space-y-3 max-w-xl">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Your rating</label>
                  <StarPicker value={formRating} onChange={setFormRating} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Your experience</label>
                  <textarea
                    value={formText}
                    onChange={(e) => setFormText(e.target.value)}
                    rows={4}
                    maxLength={4000}
                    placeholder="Share what using BharatVidyarthi was like for you..."
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                {submitError ? <p className="text-sm text-red-600">{submitError}</p> : null}
                <div className="flex gap-2">
                  <Button type="submit" disabled={submitting} variant="primary">
                    {submitting ? "Submitting..." : "Submit testimonial"}
                  </Button>
                  <Button type="button" onClick={() => setShowForm(false)} variant="secondary">
                    Cancel
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Your testimonial is checked by our team before it appears publicly.
                </p>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Testimonials;
